import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import API from '../utils/api';

export default function ChatWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    API.get('/chatbot').then(r => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const addBotMessage = (text, delay = 700) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages(m => [...m, { id: `${Date.now()}-${Math.random()}`, from: 'bot', text }]);
    }, delay);
  };

  const addUserMessage = text => {
    setMessages(m => [...m, { id: `${Date.now()}-${Math.random()}`, from: 'user', text }]);
  };

  const handleOpen = () => {
    setOpen(true);
    if (messages.length === 0 && config) {
      setMessages([{ id: 'greeting', from: 'bot', text: config.greeting }]);
    }
  };

  const matchFaq = (text) => {
    if (!config?.faqs?.length) return null;
    const lower = text.toLowerCase();
    let best = null;
    let bestScore = 0;
    for (const faq of config.faqs) {
      const score = (faq.keywords || []).filter(kw => lower.includes(kw.toLowerCase())).length;
      if (score > bestScore) { bestScore = score; best = faq; }
    }
    return bestScore > 0 ? best : null;
  };

  const processUserInput = (text) => {
    addUserMessage(text);
    setInput('');

    const qr = config?.quickReplies?.find(r => r.label.toLowerCase() === text.toLowerCase());
    if (qr) {
      if (qr.type === 'route') {
        addBotMessage(t('chat.takingYouThere'));
        setTimeout(() => { navigate(qr.value); setOpen(false); }, 1000);
      } else {
        addBotMessage(qr.value);
      }
      return;
    }

    const faq = matchFaq(text);
    if (faq) {
      addBotMessage(faq.answer);
    } else {
      addBotMessage(config?.fallbackMessage || t('chat.fallbackMessage'));
    }
  };

  const handleQuickReply = (qr) => {
    addUserMessage(qr.label);
    if (qr.type === 'route') {
      addBotMessage(t('chat.takingYouThere'), 500);
      setTimeout(() => { navigate(qr.value); setOpen(false); }, 1200);
    } else {
      addBotMessage(qr.value, 500);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const trimmed = input.trim();
      if (trimmed) processUserInput(trimmed);
    }
  };

  // Show quick reply chips below the last bot message (only when bot spoke last)
  const showQuickReplies = messages.length > 0
    && messages[messages.length - 1].from === 'bot'
    && !isTyping
    && config?.quickReplies?.length > 0;

  if (!config || !config.enabled) return null;

  return (
    <>
      {/* Floating action button */}
      <button
        className={`chat-widget-fab${open ? ' open' : ''}`}
        onClick={() => (open ? setOpen(false) : handleOpen())}
        aria-label={open ? t('chat.closeLabel') : t('chat.openLabel')}
      >
        {open ? '✕' : '💬'}
      </button>

      {open && (
        <div className="chat-widget-panel" role="dialog" aria-label={t('chat.dialogLabel')}>
          {/* Header */}
          <div className="chat-widget-header">
            <div className="chat-widget-header-avatar">🐂</div>
            <div className="chat-widget-header-info">
              <div className="chat-widget-header-name">{config.botName || 'Nandi'}</div>
              <div className="chat-widget-header-status">{t('chat.statusOnline')}</div>
            </div>
            <button className="chat-widget-close" onClick={() => setOpen(false)} aria-label={t('chat.closeButtonLabel')}>✕</button>
          </div>

          {/* Messages */}
          <div className="chat-widget-messages">
            {messages.map(msg => (
              <div key={msg.id} className={`chat-bubble chat-bubble-${msg.from}`}>
                {msg.from === 'bot' && <div className="chat-bubble-avatar">🐂</div>}
                <div className="chat-bubble-text">{msg.text}</div>
              </div>
            ))}

            {isTyping && (
              <div className="chat-bubble chat-bubble-bot">
                <div className="chat-bubble-avatar">🐂</div>
                <div className="chat-bubble-text chat-typing-dots">
                  <span /><span /><span />
                </div>
              </div>
            )}

            {showQuickReplies && (
              <div className="chat-quick-replies">
                {[...config.quickReplies]
                  .sort((a, b) => a.order - b.order)
                  .map((qr, i) => (
                    <button key={i} className="chat-quick-reply-btn" onClick={() => handleQuickReply(qr)}>
                      {qr.label}
                    </button>
                  ))}
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input row */}
          <div className="chat-widget-input-row">
            <input
              ref={inputRef}
              className="chat-widget-input"
              placeholder={t('chat.inputPlaceholder')}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              className="chat-widget-send"
              onClick={() => { const trimmed = input.trim(); if (trimmed) processUserInput(trimmed); }}
              aria-label={t('chat.sendLabel')}
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
}
